import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface DivergingBarItem {
    key: string
    label: string
    value: number
    description?: string
}

interface DivergingBarListProps {
    items: DivergingBarItem[]
    formatValue: (value: number) => string
    positiveColor?: string
    negativeColor?: string
    className?: string
}

// Gains grow right and losses grow left from a shared zero line
export const DivergingBarList: FC<DivergingBarListProps> = ({
    items,
    formatValue,
    positiveColor = 'var(--chart-2)',
    negativeColor = 'var(--chart-1)',
    className
}): ReactNode => {
    const max = Math.max(...items.map((item) => Math.abs(item.value)), 0)

    return (
        <ul className={cn('space-y-4', className)}>
            {items.map((item) => {
                const width = max === 0 ? 0 : (Math.abs(item.value) / max) * 100
                const positive = item.value >= 0

                return (
                    <li key={item.key}>
                        <div className='flex items-baseline justify-between gap-3 text-sm'>
                            <span className='min-w-0'>
                                <span className='block truncate font-medium text-foreground'>
                                    {item.label}
                                </span>
                                {item.description && (
                                    <span className='block truncate text-xs text-muted-foreground'>
                                        {item.description}
                                    </span>
                                )}
                            </span>

                            <span className='shrink-0 font-semibold text-foreground tabular-nums'>
                                {formatValue(item.value)}
                            </span>
                        </div>

                        <div className='relative mt-2 grid h-2 grid-cols-2' aria-hidden='true'>
                            <div className='flex justify-end'>
                                {!positive && (
                                    <div className='h-full rounded-l-[4px]' style={{ width: `${width}%`, background: negativeColor }} />
                                )}
                            </div>

                            <div className='flex justify-start'>
                                {positive && (
                                    <div className='h-full rounded-r-[4px]' style={{ width: `${width}%`, background: positiveColor }} />
                                )}
                            </div>

                            <span className='absolute -inset-y-1 left-1/2 w-px -translate-x-1/2 bg-foreground/25' />
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}
